import React from 'react';
import QRTrackingComponent from './trafficdrivers/QRTrackingComponent';
import TrafficAnalytics from './trafficdrivers/TrafficAnalytics';
import AffiliatePerformance from './trafficdrivers/AffiliatePerformance';
import ReferralWorkflowManager from './ReferralWorkflowManager';
import MailingList from './trafficdrivers/MailingList';
import CustomerCRM from './trafficdrivers/CustomerCRM';
import CreateAffiliate from './trafficdrivers/CreateAffiliate';
import MetricCard from './ui/MetricCard';
import Panel from './ui/Panel';
import EmptyState from './ui/EmptyState';
import ErrorState from './ui/ErrorState';
import { MetricRowSkeleton } from './ui/Skeleton';
import Icon from './icons/Icon';

interface QRCodeAnalytics {
  totalCodes: number;
  activeCodes: number;
  totalClicks: number;
  totalConversions: number;
  topPerformingChannel: string;
  recentActivity: {
    code: string;
    channel: string;
    clicks: number;
    date: string;
  }[];
}

interface TrafficDriversSectionProps {
  activeTab: string;
  qrAnalytics: QRCodeAnalytics | null;
  setActiveTab: (tab: string) => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const numberFormat = new Intl.NumberFormat('en-US');

const TrafficDriversSection: React.FC<TrafficDriversSectionProps> = ({
  activeTab,
  qrAnalytics,
  setActiveTab,
  isLoading = false,
  error = null,
  onRetry,
}) => {
  const renderOverview = () => {
    if (isLoading) {
      return <MetricRowSkeleton label="traffic metrics" />;
    }

    if (error) {
      return <ErrorState title="Traffic metrics could not load" detail={error} onRetry={onRetry} />;
    }

    if (!qrAnalytics || qrAnalytics.totalCodes === 0) {
      return (
        <EmptyState
          icon="qr"
          title="No QR codes yet"
          description="Once you create a QR code, this row shows how many codes are live, how many times they have been scanned, and which channel is bringing in the most traffic."
          action={{ label: 'Create a QR code', onClick: () => setActiveTab('qr-tracking') }}
        />
      );
    }

    // Conversion rate is derivable from what the endpoint already returns, and
    // it is the only number here that answers a decision ("is this channel
    // worth more codes?") rather than reporting a raw count.
    const conversionRate = qrAnalytics.totalClicks
      ? (qrAnalytics.totalConversions / qrAnalytics.totalClicks) * 100
      : 0;

    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Scans"
          value={numberFormat.format(qrAnalytics.totalClicks)}
          hint="All time — this endpoint does not return a prior period to compare against"
          onClick={() => setActiveTab('analytics')}
        />
        <MetricCard
          label="Conversions"
          value={numberFormat.format(qrAnalytics.totalConversions)}
          hint={`${conversionRate.toFixed(1)}% of scans converted`}
          tone={conversionRate > 0 ? 'positive' : 'neutral'}
          onClick={() => setActiveTab('analytics')}
        />
        <MetricCard
          label="Live QR codes"
          value={numberFormat.format(qrAnalytics.activeCodes)}
          hint={`of ${numberFormat.format(qrAnalytics.totalCodes)} created`}
          target={
            qrAnalytics.totalCodes
              ? {
                  label: 'Share of codes still active',
                  percent: (qrAnalytics.activeCodes / qrAnalytics.totalCodes) * 100,
                }
              : undefined
          }
          onClick={() => setActiveTab('qr-tracking')}
        />
        <MetricCard
          label="Top channel"
          // An em dash, not "N/A": nothing has been scanned yet, which is a
          // real answer rather than a missing one.
          value={qrAnalytics.topPerformingChannel || '—'}
          hint={qrAnalytics.topPerformingChannel ? 'Most scans in the period' : 'No scans recorded yet'}
          onClick={() => setActiveTab('analytics')}
        />
      </div>
    );
  };

  return (
    <>
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {renderOverview()}

          <Panel
            title="Quick actions"
            description="The three things most often done from this section"
          >
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={() => setActiveTab('qr-tracking')}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
              >
                <Icon name="qr" className="h-4 w-4" />
                Create QR code
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('affiliates')}
                className="inline-flex items-center gap-2 rounded-lg border border-border-default px-4 py-2 text-sm font-semibold text-ink-2 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
              >
                <Icon name="affiliates" className="h-4 w-4 text-ink-3" />
                Affiliate performance
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('analytics')}
                className="inline-flex items-center gap-2 rounded-lg border border-border-default px-4 py-2 text-sm font-semibold text-ink-2 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
              >
                <Icon name="analytics" className="h-4 w-4 text-ink-3" />
                View analytics
              </button>
            </div>
          </Panel>
        </div>
      )}

      {activeTab === 'analytics' && <TrafficAnalytics />}

      {/* Affiliates: who is driving views, and what they are owed for them */}
      {activeTab === 'affiliates' && <AffiliatePerformance />}

      {activeTab === 'qr-tracking' && <QRTrackingComponent />}

      {/* The referral queue, previously reachable only from /admin. It belongs
          with the affiliates who generate the referrals rather than in its own
          corner of the console. */}
      {activeTab === 'referrals' && <ReferralWorkflowManager />}

      {activeTab === 'mailing-list' && <MailingList />}

      {/* Everyone who claimed the email-and-text offer: their code, whether
          it was delivered, and whether they can still be texted. */}
      {activeTab === 'customer-crm' && <CustomerCRM />}

      {/* Recruiting, previously only on /admin. The console could issue a code
          to an affiliate but could not create one. */}
      {activeTab === 'recruit' && <CreateAffiliate />}
    </>
  );
};

export default TrafficDriversSection;
